import React, {
    createContext,
    useContext
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { getCompletedSteps, getIsBuyNowFlow, isBuyFlowPDP } from './routingSelectors';
import { saveCompletedSteps, changeStep } from './routingModule';
import emptyFunc from '../utils/emptyFunc';
import { push, replace } from './routing';
import {
    HOME_STEP,
    CATEGORY_STEP,
    PDP_STEP,
    PAYMENT,
    ORDER_SUCCESS,
    CART_STEP,
    ORDER_FAILURE,
    ORDER_CANCELLATION,
    ROUTES,
    currencyPickerAllowedPages,
    CONFIRMATION_STEP, manageGiftcardsAllowedPages, cartIconNotAllowedPagesInMobileView, REDIRECTION_ORDER_STEP
} from './flowConstants';
import {
    setIsCartIconEnabledForMobile,
    setIsCurrencyPickerEnabled,
    setIsManageGiftcardsEnabled
} from '../localeCurrencyPicker/LocaleCurrencyPickerModule';
import { getIsOldUiPreferred } from '../app/bootstrap';
import { getTotalCardQuantity } from '../productCartHandler/productCartSelectors';

export const FLOW_CONFIG = {
    [HOME_STEP]: {
        next: ({ seeAll }) => (seeAll ? CATEGORY_STEP : PDP_STEP),
        previous: emptyFunc
    },
    [CATEGORY_STEP]: {
        next: () => PDP_STEP,
        previous: () => HOME_STEP
    },
    [PDP_STEP]: {
        next: ({ skipCart = false, isBuyNow = false }) => (skipCart || isBuyNow ? PAYMENT : CART_STEP),
        previous: ({ skipCart = false, isBuyFlowPdp = false }) => (skipCart || !isBuyFlowPdp) && HOME_STEP
    },
    [CART_STEP]: {
        next: ({ goToHome = false, isBuyFlowPdp = false }) => {
            if (goToHome) {
                return isBuyFlowPdp ? PDP_STEP : HOME_STEP;
            }
            return PAYMENT;
        },
        previous: ({ forceStep = false }) => !forceStep && PDP_STEP
    },
    [REDIRECTION_ORDER_STEP]: {
        next: ({ paymentSuccess, skipCart = false }) => {
            if (skipCart) {
                return paymentSuccess ? ORDER_SUCCESS : ORDER_FAILURE;
            }
            return CONFIRMATION_STEP;
        },
        previous: () => PAYMENT
    },
    [PAYMENT]: {
        next: ({ paymentSuccess, skipCart = false, isCancellation = false }) => {
            if (isCancellation) return ORDER_CANCELLATION;
            if (skipCart) {
                return paymentSuccess ? ORDER_SUCCESS : ORDER_FAILURE;
            }
            return CONFIRMATION_STEP;
        },
        previous: ({ skipCart = false, isBuyNow = false }) => (skipCart || isBuyNow ? PDP_STEP : CART_STEP)
    },
    [CONFIRMATION_STEP]: {
        next: ({ isBuyFlowPdp = false }) => (isBuyFlowPdp ? PDP_STEP : HOME_STEP),
        previous: () => PAYMENT
    },
    [ORDER_SUCCESS]: {
        next: () => HOME_STEP,
        previous: () => PAYMENT
    },
    [ORDER_FAILURE]: {
        next: () => HOME_STEP,
        previous: () => PAYMENT
    },
    [ORDER_CANCELLATION]: {
        next: () => HOME_STEP,
        previous: () => PAYMENT
    }
};

export const FlowContext = createContext();



let sagaContextMethods = {};
export const getFlowContextMethodsForSaga = () => sagaContextMethods;

export const FlowProvider = ({ children, flowConfig }) => {
    const dispatch = useDispatch();
    const completedSteps = useSelector(state => getCompletedSteps(state)).toJS();
    const isBuyNow = useSelector(state => getIsBuyNowFlow(state));
    const isBuyFlowPdp = useSelector(state => isBuyFlowPDP(state));
    const totalCardQuantity = useSelector(state => getTotalCardQuantity(state));
    const setCompletedSteps = steps => dispatch(saveCompletedSteps(steps));
    const setCurrencyPicker = status => dispatch(setIsCurrencyPickerEnabled(status));
    const setManageGiftcards = status => dispatch(setIsManageGiftcardsEnabled(status));
    const setCartIconForMobile = status => dispatch(setIsCartIconEnabledForMobile(status));


    const setStepCompleted = (stepName, { isCompleted = true } = {}) => {
        if (stepName === HOME_STEP) {
            setCompletedSteps({ [stepName]: isCompleted });
        } else if (stepName === CATEGORY_STEP) {
            setCompletedSteps({ [HOME_STEP]: true, [stepName]: isCompleted });
        } else {
            setCompletedSteps({ ...completedSteps, [stepName]: isCompleted });
        }
    };

    const pushOrReplacePath = (path, shouldPushHistory, overwritingHistoryParams = {}) => {
        if (shouldPushHistory) {
            push({ pathname: path, ...overwritingHistoryParams });
        } else {
            replace({ pathname: path, ...overwritingHistoryParams });
        }
    };

    const goToStep = (stepName, additionalFlowParams = {}) => {
        const { shouldPushHistory = true, overwritingHistoryParams = {} } = additionalFlowParams;
        if (stepName) {
            const route = ROUTES[stepName];
            pushOrReplacePath(route, shouldPushHistory, overwritingHistoryParams);
        }
    };

    const goToNextStep = (stepName, additionalFlowParams = {}) => {
        const nextStep = flowConfig[stepName].next({
            isBuyNow,
            
            skipCart: getIsOldUiPreferred(),
            isBuyFlowPdp,
            ...additionalFlowParams
        });

        const { bypassStepCompletion = false } = additionalFlowParams;
        if (!bypassStepCompletion) {
            setStepCompleted(stepName);
        }
        goToStep(nextStep, additionalFlowParams);
    };

    const goToPreviousStep = (stepName, additionalFlowParams = {}) => {
        const previous = flowConfig[stepName].previous({
            isBuyNow,
            
            skipCart: getIsOldUiPreferred(),
            isBuyFlowPdp,
            ...additionalFlowParams
        });

        goToStep(previous, additionalFlowParams);
    };

        const findFirstIncompleteStep = (stepName, additionalFlowParams = {}) => {
        let currentStep = flowConfig[stepName];
        let currentStepName = stepName;
        while (currentStep) {
            const previous = currentStep.previous({
                isBuyNow,
                
                skipCart: getIsOldUiPreferred(),
                forceStep: true,
                isBuyFlowPdp,
                ...additionalFlowParams
            });
            if ((!previous || completedSteps[previous]) && !(previous === CART_STEP && totalCardQuantity === 0)) {
                return currentStepName;
            }
            currentStep = flowConfig[previous];
            currentStepName = previous;
        }
        
        return '';
    };

    const stepHasChanged = (stepName, location) => dispatch(changeStep(stepName, location));

    const showCurrencyPicker = (stepName) => {
        if (currencyPickerAllowedPages.includes(stepName)) {
            setCurrencyPicker({ isCurrencyPickerEnabled: true });
        } else {
            setCurrencyPicker({ isCurrencyPickerEnabled: false });
        }
    };

    const showManageGiftcards = (stepName) => {
        if (manageGiftcardsAllowedPages.includes(stepName)) {
            setManageGiftcards({ isManageGiftcardsEnabled: true });
        } else {
            setManageGiftcards({ isManageGiftcardsEnabled: false });
        }
    };

    const showCartInMobileHeader = (stepName) => {
        if (cartIconNotAllowedPagesInMobileView.includes(stepName)) {
            setCartIconForMobile({ isCartIconEnabledForMobile: false });
        } else {
            setCartIconForMobile({ isCartIconEnabledForMobile: true });
        }
    };

    // This object will be used by redux saga to trigger routing actions
    sagaContextMethods = {
        goToNextStep,
        goToPreviousStep
    };

    return (
        <FlowContext.Provider
            value={{
                goToNextStep,
                goToPreviousStep,
                findFirstIncompleteStep,
                stepHasChanged,
                showCurrencyPicker,
                showManageGiftcards,
                showCartInMobileHeader,
                goToStep
            }}
        >
            {children}
        </FlowContext.Provider>
    );
};

FlowProvider.displayName = 'FlowProvider';

FlowProvider.propTypes = {
    children: PropTypes.node.isRequired,
    flowConfig: PropTypes.object
};

FlowProvider.defaultProps = {
    flowConfig: FLOW_CONFIG
};

export const useFlow = () => useContext(FlowContext);
